import { useEffect, useRef } from 'react';
import { getEcho, leaveChannel } from '../lib/echo';
import type { CalendarEvent, FileFolder, FileItem, FilePermission, Message, Notification, SubTask, Task, TaskComment } from '../types';

export interface TaskEventHandlers {
  onTaskCreated?: (task: Task) => void;
  onTaskUpdated?: (task: Task) => void;
  onTaskDeleted?: (taskId: number, projectId?: number | null) => void;
  onTaskStatusChanged?: (
    taskId: number,
    payload: { old_status: string; new_status: string; approval_status: string | null; approved_by: number | null; approved_at: string | null }
  ) => void;
  onSubTaskCreated?: (taskId: number, subTask: SubTask) => void;
  onSubTaskToggled?: (taskId: number, subTask: SubTask) => void;
  onTaskCommentCreated?: (taskId: number, comment: TaskComment) => void;
}

export interface FileEventHandlers {
  onFileCreated?: (file: FileItem, folderId?: number | null) => void;
  onFileUpdated?: (file: FileItem, folderId?: number | null) => void;
  onFileDeleted?: (fileId: number, folderId?: number | null, projectId?: number | null) => void;
  onFolderCreated?: (folder: FileFolder, parentId?: number | null) => void;
  onFolderUpdated?: (folder: FileFolder, parentId?: number | null) => void;
  onFolderDeleted?: (folderId: number, parentId?: number | null, projectId?: number | null) => void;
  onFilePermissionUpdated?: (
    itemId: number,
    itemType: 'file' | 'folder',
    permission: FilePermission | null,
    deleted: boolean
  ) => void;
}

export interface CalendarEventHandlers {
  onCalendarEventCreated?: (event: CalendarEvent) => void;
  onCalendarEventUpdated?: (event: CalendarEvent) => void;
  onCalendarEventDeleted?: (eventId: number, projectId?: number | null) => void;
}

export interface NotificationEventHandlers {
  onNotificationReceived?: (notification: Notification, unreadCount: number, menuKey?: string | null) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useRealtimeChannel(channelName: string | null, listeners: Record<string, (event: any) => void>) {
  const listenersRef = useRef(listeners);
  listenersRef.current = listeners;

  useEffect(() => {
    if (!channelName) return;

    const echo = getEcho();
    if (!echo) return;

    const channel = echo.private(channelName);

    Object.keys(listeners).forEach((event) => {
      channel.listen(event, (eventData: unknown) => {
        listenersRef.current[event]?.(eventData);
      });
    });

    return () => {
      leaveChannel(channelName);
    };
    // listeners are read from ref to avoid re-subscribing on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName]);
}

export function useRealtimeTasks(projectId: number | null | undefined, handlers: TaskEventHandlers) {
  useRealtimeChannel(
    projectId ? `project.${projectId}.tasks` : null,
    {
      '.task.created': (e: { task: Task }) => handlers.onTaskCreated?.(e.task),
      '.task.updated': (e: { task: Task }) => handlers.onTaskUpdated?.(e.task),
      '.task.deleted': (e: { task_id: number; project_id?: number | null }) => handlers.onTaskDeleted?.(e.task_id, e.project_id),
      '.task.status.changed': (e: {
        task_id: number;
        old_status: string;
        new_status: string;
        approval_status: string | null;
        approved_by: number | null;
        approved_at: string | null;
      }) => handlers.onTaskStatusChanged?.(e.task_id, e),
      '.sub-task.created': (e: { task_id: number; sub_task: SubTask }) => handlers.onSubTaskCreated?.(e.task_id, e.sub_task),
      '.sub-task.toggled': (e: { task_id: number; sub_task: SubTask }) => handlers.onSubTaskToggled?.(e.task_id, e.sub_task),
      '.task.comment.created': (e: { task_id: number; comment: TaskComment }) =>
        handlers.onTaskCommentCreated?.(e.task_id, e.comment),
    }
  );
}

export function useRealtimeFiles(projectId: number | null | undefined, handlers: FileEventHandlers) {
  useRealtimeChannel(
    projectId ? `project.${projectId}.files` : null,
    {
      '.file.created': (e: { file: FileItem; folder_id?: number | null }) => handlers.onFileCreated?.(e.file, e.folder_id),
      '.file.updated': (e: { file: FileItem; folder_id?: number | null }) => handlers.onFileUpdated?.(e.file, e.folder_id),
      '.file.deleted': (e: { file_id: number; folder_id?: number | null; project_id?: number | null }) =>
        handlers.onFileDeleted?.(e.file_id, e.folder_id, e.project_id),
      '.folder.created': (e: { folder: FileFolder; parent_id?: number | null }) => handlers.onFolderCreated?.(e.folder, e.parent_id),
      '.folder.updated': (e: { folder: FileFolder; parent_id?: number | null }) => handlers.onFolderUpdated?.(e.folder, e.parent_id),
      '.folder.deleted': (e: { folder_id: number; parent_id?: number | null; project_id?: number | null }) =>
        handlers.onFolderDeleted?.(e.folder_id, e.parent_id, e.project_id),
      '.file.permission.updated': (e: {
        permission: FilePermission | null;
        item_id: number;
        item_type: 'file' | 'folder';
        deleted: boolean;
      }) => handlers.onFilePermissionUpdated?.(e.item_id, e.item_type, e.permission, e.deleted),
    }
  );
}

export function useRealtimeCalendar(projectId: number | null | undefined, handlers: CalendarEventHandlers) {
  useRealtimeChannel(
    projectId ? `project.${projectId}.calendar` : null,
    {
      '.calendar.event.created': (e: { event: CalendarEvent }) => handlers.onCalendarEventCreated?.(e.event),
      '.calendar.event.updated': (e: { event: CalendarEvent }) => handlers.onCalendarEventUpdated?.(e.event),
      '.calendar.event.deleted': (e: { event_id: number; project_id?: number | null }) =>
        handlers.onCalendarEventDeleted?.(e.event_id, e.project_id),
    }
  );
}

export function useRealtimeNotifications(userId: number | null | undefined, handlers: NotificationEventHandlers) {
  useRealtimeChannel(
    userId ? `App.Models.User.${userId}` : null,
    {
      '.notification.received': (e: { notification: Notification; unread_count: number; menu_key?: string | null }) =>
        handlers.onNotificationReceived?.(e.notification, e.unread_count, e.menu_key),
    }
  );
}

export interface MessageEventHandlers {
  onMessageSent?: (payload: { message: Message; conversation_id: number }) => void;
  onMessageUpdated?: (payload: { message: Message; conversation_id: number }) => void;
  onMessageDeleted?: (payload: { message_id: number; conversation_id: number }) => void;
  onMessageReactionUpdated?: (payload: {
    message_id: number;
    conversation_id: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reactions: any[];
  }) => void;
  onHuddleUpdated?: (payload: { call_id: number; conversation_id: number; active: boolean }) => void;
}

export function useRealtimeMessages(userId: number | null | undefined, handlers: MessageEventHandlers) {
  useRealtimeChannel(
    userId ? `App.Models.User.${userId}` : null,
    {
      '.message.sent': (e: { message: Message; conversation_id?: number }) => {
        const convId = e.conversation_id ?? e.message.conversation_id;
        handlers.onMessageSent?.({ message: e.message, conversation_id: convId });
      },
      '.message.updated': (e: { message: Message; conversation_id?: number }) => {
        const convId = e.conversation_id ?? e.message.conversation_id;
        handlers.onMessageUpdated?.({ message: e.message, conversation_id: convId });
      },
      '.message.deleted': (e: { message_id: number; conversation_id: number }) =>
        handlers.onMessageDeleted?.(e),
      '.message.reaction': (e: {
        message_id: number;
        conversation_id?: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        reactions: any[];
      }) => {
        const convId = e.conversation_id ?? 0;
        handlers.onMessageReactionUpdated?.({ ...e, conversation_id: convId });
      },
      '.huddle.updated': (e: { call_id: number; conversation_id: number; active: boolean }) =>
        handlers.onHuddleUpdated?.(e),
    }
  );
}
